/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import idx from 'idx';
import invariant from 'invariant';

import AddGroupOwnerModal from 'groups/components/AddGroupOwnerModal';
import Button from 'components/ButtonDeprecated';
import Feature from 'components/Feature';
import Group from 'entities/Group';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ModalTrigger from 'components/ModalTrigger';
import PeopleTable from 'components/PeopleTable';
import PeopleList from 'components/PeopleList';
import View from 'components/View';
import { withRouter } from 'app/context/Router';

import ADMIN_ORGANIZATION_QUERY from 'groups/components/queries/AdminOrganizationQuery.gql';

import type { GroupInfo } from 'components/PeopleList/PeopleList';
import type {
  AdminOrganizationQuery,
  AdminOrganizationQuery_organizations,
} from 'groups/components/queries/__generated__/AdminOrganizationQuery';

type Props = {
  orgId: number,
  group: Group,
  data: ReactApolloData<AdminOrganizationQuery>,
  router: *,
};

type OldPeopleTableProps = {
  group: Group,
  addOwnerButton: any,
  adminConsoleAccess: ?boolean,
};

type NewPeopleTableProps = {
  organization: ?AdminOrganizationQuery_organizations,
  group: Group,
  isAdmin: boolean,
};

function OldPeopleTable({ addOwnerButton, adminConsoleAccess, group }: OldPeopleTableProps) {
  return (
    <View className="GroupPeopleTable" scroll gutter="small">
      <PeopleTable
        adminConsoleAccess={adminConsoleAccess}
        group={group}
        namespace={group.getUuid()}
        headerActions={group.canAddOwner() ? addOwnerButton : null}
      />
    </View>
  );
}

function groupInfoForNewPeopleTable(group: Group): GroupInfo {
  return {
    // $FlowFixMe - no type coercion for 0.93.0
    id: group.id,
    uuid: group.getUuid(),
    // $FlowFixMe
    name: group.getCode(),
    className: group.getName(),
    // $FlowFixMe
    groupType: group.getType(),
    staffClassOrgId: group.getStaffClassOrgId(),
    hasChildren: group.hasChildren(),
    pdfUrl: group.getPdfUrl(),
    joinUrl: group.getJoinUrl(),
    groupAvatar: group.getAvatar() ? { svgUrl: group.getAvatarUrl() } : null,
    subscribe: group.getSubscriptionInfo(),
    canAddOwner: group.canAddOwner(),
    canAddPeople: group.canAddPeople(),
  };
}

function NewPeopleTable({ organization, group, isAdmin }: NewPeopleTableProps) {
  if (organization) {
    return (
      <PeopleList
        entityType="organization"
        entityUuid={organization.uuid}
        groupInfo={groupInfoForNewPeopleTable(group)}
        associatedOrg={organization}
        isAdmin={isAdmin}
      />
    );
  } else {
    const groupUuid = group.getUuid();
    invariant(groupUuid, 'Tried to render group people table without a group uuid!');
    // TODO: We will need to re-think what happens for batch actions when
    //   there's no org. Currently they all require one.
    const associatedOrg = group.getOrganization();
    return (
      <PeopleList
        entityType="group"
        entityUuid={groupUuid}
        groupInfo={groupInfoForNewPeopleTable(group)}
        associatedOrg={associatedOrg}
        isAdmin={isAdmin}
      />
    );
  }
}

class GroupPeopleTable extends React.Component<Props, *> {
  shouldComponentUpdate(nextProps: Props) {
    const groupOrOrgChanged =
      nextProps.orgId !== this.props.orgId || nextProps.group.id !== this.props.group.id;

    if (!nextProps.data || !this.props.data) {
      return groupOrOrgChanged;
    }

    return groupOrOrgChanged || nextProps.data.organizations !== this.props.data.organizations;
  }

  render() {
    const { data, group } = this.props;

    if (group.isRoot()) {
      this.props.router.push('/classes/r/messages');
    }

    if (data && data.loading) {
      return <CenteredLoadingIndicator />;
    }

    const organization = data && data.organizations ? data.organizations[0] : null;
    const adminConsoleAccess = idx(organization, _ => _.permissions.adminConsole.accessConsole);

    const addOwnerButton = (
      // $FlowIssue inferring onRequestHide
      <ModalTrigger data-test="add-owner-button" modal={<AddGroupOwnerModal group={group} />}>
        <Button
          level="secondary"
          size="small"
          eventElementName="GroupMembersTabList-header-actions-addOwner">
          Add owner
        </Button>
      </ModalTrigger>
    );

    const oldTable = (
      <OldPeopleTable
        data-test="oldPeopleTable"
        adminConsoleAccess={adminConsoleAccess}
        addOwnerButton={addOwnerButton}
        group={group}
      />
    );

    const newTable = (
      <NewPeopleTable
        data-test="newPeopleTable"
        organization={organization}
        group={group}
        isAdmin={adminConsoleAccess || false}
      />
    );

    if (group.isStaffClass()) {
      return adminConsoleAccess ? newTable : oldTable;
    } else {
      return <Feature name="2019.mar.web.class_level_people_list" on={newTable} off={oldTable} />;
    }
  }
}

const withOrganizationAndClass = graphql(ADMIN_ORGANIZATION_QUERY, {
  skip: ({ orgId }) => !orgId,
  options: ({ orgId }) => ({
    variables: { organizationId: orgId },
  }),
});

export { GroupPeopleTable };
export default compose(withOrganizationAndClass, withRouter)(GroupPeopleTable);
